import React, { useState } from 'react';
import { CalculatorIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendToWhatsApp } from '../../utils/whatsapp';

const Calculator = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    pipeType: 'water',
    diameter: '',
    sdr: '',
    length: '',
    deliveryType: '',
    quantity: '1'
  });


  const calculatePrice = () => {
    // Примерные цены за метр для разных типов труб
    const basePrices: Record<string, number> = {
      water: 100,
      sewer: 150,
      gas: 200,
      telecom: 80
    };

    const diameter = parseInt(formData.diameter);
    const length = parseInt(formData.length);
    const quantity = parseInt(formData.quantity);

    if (!diameter || !length) return 0;

    // Увеличиваем цену в зависимости от диаметра
    const diameterMultiplier = 1 + (diameter - 20) / 100;
    const basePrice = basePrices[formData.pipeType] || 100;
    const totalPrice = basePrice * diameterMultiplier * length * quantity;

    return Math.round(totalPrice);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const calculatedPrice = calculatePrice();

    const pipeTypes: Record<string, string> = { water: 'Водопроводная', sewer: 'Канализационная', gas: 'Газопроводная', telecom: 'Телекоммуникация' };
    const message = `Здравствуйте! Расчет стоимости:\nТип: ${pipeTypes[formData.pipeType] || formData.pipeType}\nДиаметр: ${formData.diameter} мм\nSDR: ${formData.sdr}\nДлина: ${formData.length} м\nПоставка: ${formData.deliveryType}\nКол-во: ${formData.quantity}\nРасчет: ${calculatedPrice} тг`;
    sendToWhatsApp(message, 'Калькулятор стоимости');
  };

  return (
    <section className="py-16 bg-primary-50 dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="lg:grid lg:grid-cols-2">
            <div className="bg-primary-600 p-8 lg:p-12 text-white">
              <div className="flex items-center mb-6">
                <CalculatorIcon className="h-10 w-10 mr-3" />
                <h2 className="text-3xl font-bold">{t('calculator.title')}</h2>
              </div>
              <p className="text-xl mb-6">
                {t('calculator.subtitle')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{t('calculator.feature1')}</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{t('calculator.feature2')}</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{t('calculator.feature3')}</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  <span>{t('calculator.feature4')}</span>
                </li>
              </ul>
            </div>
            
            <div className="p-8 lg:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                    {t('calc.pipeType')}
                  </label>
                  <div className="relative">
                    <select
                      value={formData.pipeType}
                      onChange={(e) => setFormData({...formData, pipeType: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white appearance-none pr-10"
                    >
                      <option value="water">{t('calc.water')}</option>
                      <option value="sewer">{t('calc.sewer')}</option>
                      <option value="gas">{t('calc.gas')}</option>
                      <option value="telecom">{t('calc.telecom')}</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                      {t('calc.diameter')}
                    </label>
                    <input
                      type="number"
                      value={formData.diameter}
                      onChange={(e) => setFormData({...formData, diameter: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white"
                      placeholder="110"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                      {t('calc.sdr')}
                    </label>
                    <div className="relative">
                      <select
                        value={formData.sdr}
                        onChange={(e) => setFormData({...formData, sdr: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white appearance-none pr-10"
                      >
                        <option value="">{t('calc.selectSdr')}</option>
                        <option value="SDR 41">SDR 41</option>
                        <option value="SDR 33">SDR 33</option>
                        <option value="SDR 26">SDR 26</option>
                        <option value="SDR 21">SDR 21</option>
                        <option value="SDR 17.6">SDR 17.6</option>
                        <option value="SDR 17">SDR 17</option>
                        <option value="SDR 13.6">SDR 13.6</option>
                        <option value="SDR 11">SDR 11</option>
                        <option value="SDR 9">SDR 9</option>
                        <option value="SDR 7.4">SDR 7.4</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                      {t('calc.length')}
                    </label>
                    <input
                      type="number"
                      value={formData.length}
                      onChange={(e) => setFormData({...formData, length: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white"
                      placeholder="100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                      {t('calc.delivery')}
                    </label>
                    <div className="relative">
                      <select
                        value={formData.deliveryType}
                        onChange={(e) => setFormData({...formData, deliveryType: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white appearance-none pr-10"
                      >
                        <option value="">{t('calc.selectDelivery')}</option>
                        <option value="coils">{t('calc.coils')}</option>
                        <option value="segments">{t('calc.segments')}</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                    {t('calculator.quantity')}
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white"
                    min="1"
                  />
                </div>

                <button
                  type="submit"
                  disabled={false}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-md font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {t('calculator.submit')}
                </button>

              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;